import { useCallback, useState } from "react";
import { translateForLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import type { CategoryView, PaymentOptionType, SellerShippingRuleView } from "@/lib/api/types";
import { useCreateCategoryMutation } from "@/modules/categories/services/useCreateCategoryMutation";
import { useUploadToSignedUrlMutation } from "@/shared/services/useUploadToSignedUrlMutation";
import { useCreateStoreLogoUploadIntentMutation } from "@/modules/settings/services/useCreateStoreLogoUploadIntentMutation";
import { useUpdateStoreMutation } from "@/modules/settings/services/useUpdateStoreMutation";
import { useUpdateStoreSettingsMutation } from "@/modules/settings/services/useUpdateStoreSettingsMutation";
import { useUpsertPaymentOptionMutation } from "@/modules/payment-options/services/useUpsertPaymentOptionMutation";
import { useCreateShippingRuleMutation } from "@/modules/settings/services/useShippingRuleMutations";
import { useApplyBrandColorsMutation } from "@/modules/settings/services/useApplyBrandColorsMutation";
import {
  guidedToEntries,
  type PaymentEntry,
} from "@/modules/payment-options/lib/payment-option-mapping";
import {
  isInstaConfigured,
  isWalletConfigured,
  type PanelForm,
} from "@/modules/settings/components/redesign/store-settings/payment/payment-form";
import {
  useGenerateSetupMutation,
  type GenerateSetupInput,
} from "../services/useGenerateSetupMutation";
import type { CatPair } from "./wizard-data";
import type { BrandColors, ShippingRow } from "./wizard-types";

export type SubmitInput = {
  logo: string | null;
  cats: CatPair[];
  productsLabel?: string;
  /** Per-language counterparts of `productsLabel`, each populated only when
   *  a value is actually known in that language (preset types resolve in
   *  both locales; a freeform "other" label is known only in the locale the
   *  seller typed it in). Used so the bilingual store-description templates
   *  never interpolate the wrong language. */
  productsLabelEn?: string;
  productsLabelAr?: string;
  payForm: PanelForm;
  shipping: ShippingRow[];
  whats: string;
  /** Confirmed brand-color choice from the Logo step (Phase 3). `null` when the
   *  logo was skipped or extraction produced nothing — the persist step is then
   *  a no-op (no theme write). */
  brandColors: BrandColors | null;
};

export type SubmitStatus = "idle" | "saving" | "done" | "error";

// ── data-URL → Blob ──────────────────────────────────────────────
function dataUrlToBlob(dataUrl: string): { blob: Blob; contentType: string; ext: string } {
  const [head, body] = dataUrl.split(",");
  const mimeMatch = /data:([^;]+);base64/.exec(head);
  const contentType = mimeMatch?.[1] ?? "image/png";
  const bin = atob(body);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return { blob: new Blob([bytes], { type: contentType }), contentType, ext: contentType.split("/")[1] ?? "png" };
}

// Backend expects the local Egyptian mobile format `01XXXXXXXXX` (regex
// /^01\d{9}$/) — not E.164. Normalize whatever the seller typed into that.
export function normalizeWhatsApp(whats: string): string | null {
  let d = whats.replace(/[^0-9]/g, "");
  if (d.startsWith("20")) d = d.slice(2); // strip +20 country code if present
  if (d.length === 10 && d.startsWith("1")) d = "0" + d; // national → local
  return /^01\d{9}$/.test(d) ? d : null;
}

// ── payment form → payment option payloads (mirrors Settings persist) ──
type PaymentOptionPayload = { type: PaymentOptionType; entries: PaymentEntry[] };

function buildPaymentOptions(form: PanelForm): PaymentOptionPayload[] {
  const out: PaymentOptionPayload[] = [];
  if (form.walletOn && isWalletConfigured(form.wallets)) {
    for (const w of form.wallets) {
      out.push({
        type: "WALLET",
        entries: guidedToEntries({ kind: "wallet", provider: w.provider, phone: w.phone }),
      });
    }
  }
  if (form.instaOn && isInstaConfigured(form.insta)) {
    const i = form.insta;
    out.push({
      type: "INSTAPAY",
      entries: guidedToEntries({
        kind: "instapay",
        receiver: i.receiver,
        phone: i.phone,
        ipa: i.ipa,
        cardNumber: i.cardNumber,
        cardHolder: i.cardHolder,
        bankName: i.bankName,
        holder: i.holder,
        account: i.account,
      }),
    });
  }
  // COD is persisted via store settings (acceptsCod); card is "coming soon".
  return out;
}

// True if the seller configured a real (non-COD) payment method the AI
// description endpoint can use.
function hasRealPayment(form: PanelForm): boolean {
  if (form.walletOn && isWalletConfigured(form.wallets)) return true;
  return form.instaOn && isInstaConfigured(form.insta) && form.insta.receiver !== "CARD";
}

// ── payment form → generate-endpoint payment fields ──────────────
function buildGeneratePaymentFields(
  form: PanelForm,
): Pick<GenerateSetupInput, "instaPayPayment" | "walletPayment"> {
  const out: Pick<GenerateSetupInput, "instaPayPayment" | "walletPayment"> = {};
  if (form.walletOn && isWalletConfigured(form.wallets)) {
    out.walletPayment = {
      wallets: form.wallets.map((w) => ({
        provider: w.provider,
        phoneNumber: normalizeWhatsApp(w.phone) ?? w.phone,
      })),
    };
  }
  if (form.instaOn) {
    const i = form.insta;
    if (i.receiver === "PHONE" && i.phone.trim())
      out.instaPayPayment = { receiverType: "PHONE", phoneNumber: normalizeWhatsApp(i.phone) ?? i.phone };
    else if (i.receiver === "IPA" && i.ipa.trim())
      out.instaPayPayment = { receiverType: "IPA", ipaHandle: i.ipa };
    else if (i.receiver === "BANK_ACCOUNT" && i.account.trim())
      out.instaPayPayment = {
        receiverType: "BANK_ACCOUNT",
        bankName: i.bankName,
        accountHolderName: i.holder,
        accountNumber: i.account,
      };
    // CARD: not supported by the generate endpoint — skipped.
  }
  return out;
}

// ── shipping rows → grouped shipping rules ───────────────────────
function buildShippingRules(rows: ShippingRow[]): Array<{
  price: number;
  estimatedDeliveryDays: number;
  governorateIds: string[];
}> {
  const groups = new Map<string, { price: number; days: number; ids: string[] }>();
  for (const r of rows) {
    if (!r.govId) continue;
    const price = parseInt(r.price, 10) || 0;
    const days = parseInt(r.days, 10) || 1;
    const key = `${price}|${days}`;
    const g = groups.get(key) ?? { price, days, ids: [] };
    g.ids.push(r.govId);
    groups.set(key, g);
  }
  return [...groups.values()].map((g) => ({
    price: g.price,
    estimatedDeliveryDays: g.days,
    governorateIds: g.ids,
  }));
}

export function useQuickSetupSubmit(storeSlug: string, storeName: string) {
  const t = useTranslations();
  const createCategory = useCreateCategoryMutation();
  const uploadToSignedUrl = useUploadToSignedUrlMutation();
  const createLogoUploadIntent = useCreateStoreLogoUploadIntentMutation();
  const updateStore = useUpdateStoreMutation();
  const updateStoreSettings = useUpdateStoreSettingsMutation();
  const upsertPaymentOption = useUpsertPaymentOptionMutation();
  const createShippingRule = useCreateShippingRuleMutation();
  const applyBrandColors = useApplyBrandColorsMutation();
  const generateMutation = useGenerateSetupMutation();

  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const persist = useCallback(
    async (input: SubmitInput) => {
      setStatus("saving");
      setError(null);
      try {
        await persistLogo(input.logo);
        await persistBrandColors(input.brandColors);
        await persistCategories(input.cats);
        await persistPayments(input.payForm);
        await persistShipping(input.shipping);
        await persistSettings(input);
        setStatus("done");
      } catch (e) {
        setError(t(keys.quickSetup.done.errorGeneric));
        setStatus("error");
        throw e;
      }

      // ── Steps (hoisted; close over the hook's mutations + storeSlug) ──

      // Logo — upload then attach to the store. Best-effort: the logo is optional,
      // so an upload failure (e.g. storage CORS / quota) must NOT abort setup —
      // otherwise categories/payments never get created and the ProtectedLayout
      // guard bounces the seller back into the wizard. Seller can add it later.
      async function persistLogo(logo: string | null): Promise<void> {
        if (!logo) return;
        try {
          const { blob, contentType, ext } = dataUrlToBlob(logo);
          const intent = await createLogoUploadIntent.mutateAsync({
            fileName: `logo.${ext}`,
            contentType,
            sizeBytes: blob.size,
          });
          await uploadToSignedUrl.mutateAsync({ uploadUrl: intent.uploadUrl, file: blob, contentType });
          await updateStore.mutateAsync({ storeSlug, logoObjectKey: intent.objectKey });
        } catch {
          // Logo upload failed — skip it and finish the rest of setup.
        }
      }

      // Brand colors — apply the seller's confirmed logo colors to their live
      // theme (`settings.colors`), or record the neutral choice as metadata.
      // Best-effort (mirrors persistLogo): a theme-write failure must NEVER
      // abort setup — otherwise categories/payments never get created and the
      // ProtectedLayout guard bounces the seller back into the wizard. Fires
      // only when the seller produced a choice (extracted or neutral); stays a
      // no-op on no-logo / extraction-failure (brandColors === null).
      async function persistBrandColors(brandColors: BrandColors | null): Promise<void> {
        if (!brandColors) return;
        try {
          if (brandColors.theme === "extracted" && brandColors.derived) {
            await applyBrandColors.mutateAsync({
              theme: "extracted",
              colors: brandColors.derived,
              palette: brandColors.palette,
              accent: brandColors.accent,
            });
          } else {
            // neutral — record metadata only, leave settings.colors untouched.
            await applyBrandColors.mutateAsync({
              theme: "neutral",
              palette: brandColors.palette,
              accent: null,
            });
          }
        } catch {
          // Theme write failed — skip it and finish the rest of setup.
        }
      }

      // Categories — idempotent: reuse any that already exist (matched by Arabic or
      // English name), create only the missing ones. Tolerates re-running setup.
      async function persistCategories(cats: CatPair[]): Promise<void> {
        const norm = (s: string) => s.trim().toLowerCase();
        const existing = await apiJsonClient<CategoryView[]>(endpoints.seller.categories).catch(
          () => [] as CategoryView[],
        );
        const idByName = new Map<string, string>();
        for (const c of existing) {
          idByName.set(norm(c.nameAr), c.id);
          if (c.nameEn) idByName.set(norm(c.nameEn), c.id);
        }
        let nextOrder = existing.length;
        for (const cat of cats) {
          if (idByName.get(norm(cat.en)) ?? idByName.get(norm(cat.ar))) continue;
          try {
            const created = await createCategory.mutateAsync({
              storeSlug,
              nameAr: cat.ar,
              nameEn: cat.en,
              sortOrder: nextOrder,
            });
            idByName.set(norm(cat.en), created.id);
            idByName.set(norm(cat.ar), created.id);
            nextOrder += 1;
          } catch {
            // Likely a concurrent/duplicate create — ignore and continue rather
            // than aborting the whole setup.
          }
        }
      }

      // Payment options (mirrors the Settings payment persist).
      async function persistPayments(payForm: PanelForm): Promise<void> {
        for (const opt of buildPaymentOptions(payForm)) {
          await upsertPaymentOption.mutateAsync({
            storeSlug,
            type: opt.type,
            isActive: true,
            entries: opt.entries,
          });
        }
      }

      // Shipping rules — skip governorates already covered by an existing rule (the
      // backend rejects double-assignment), so re-running is safe.
      async function persistShipping(shipping: ShippingRow[]): Promise<void> {
        const existing = await apiJsonClient<SellerShippingRuleView[]>(
          endpoints.seller.storeShippingRules,
        ).catch(() => [] as SellerShippingRuleView[]);
        const covered = new Set(existing.flatMap((r) => r.governorateIds));
        for (const rule of buildShippingRules(shipping)) {
          const governorateIds = rule.governorateIds.filter((id) => !covered.has(id));
          if (!governorateIds.length) continue;
          await createShippingRule.mutateAsync({
            storeSlug,
            name: null,
            price: rule.price,
            estimatedDeliveryDays: rule.estimatedDeliveryDays,
            governorateIds,
          });
          governorateIds.forEach((id) => covered.add(id));
        }
      }

      // Store settings — WhatsApp + cash-on-delivery + auto-confirm.
      async function persistSettings(data: SubmitInput): Promise<void> {
        await updateStoreSettings.mutateAsync({
          storeSlug,
          whatsappNumber: normalizeWhatsApp(data.whats),
          acceptsCod: data.payForm.cod,
          autoConfirmCodOrders: data.payForm.autoConfirmCod,
        });
      }
    },
    [
      t,
      storeSlug,
      createCategory,
      uploadToSignedUrl,
      createLogoUploadIntent,
      updateStore,
      updateStoreSettings,
      upsertPaymentOption,
      createShippingRule,
      applyBrandColors,
    ],
  );

  // AI-written description via the onboarding generate endpoint (server-side
  // model). Falls back to a locally built description if the call fails.
  const buildFallback = useCallback(
    (input: SubmitInput): { ar: string; en: string } => {
      // Literal bilingual templates (not i18n) so each field is always in its
      // own language regardless of which locale the seller runs the wizard in.
      // BUG S7: `productsLabel` alone is a single value in whichever locale the
      // seller ran the wizard in, so it can't be interpolated into both
      // templates — that leaked Arabic into the English description/SEO meta.
      // Use the per-language labels instead, each falling back to the
      // matching-language generic fallback (never the other language's text).
      const whatAr = input.productsLabelAr || translateForLocale("ar", keys.quickSetup.fallbackProducts);
      const whatEn = input.productsLabelEn || translateForLocale("en", keys.quickSetup.fallbackProducts);
      return {
        ar: `${storeName} — ${whatAr} بأفضل الأسعار، والتوصيل لحد باب بيتك. اطلب دلوقتي!`,
        en: `${storeName} — quality ${whatEn} at the best prices, delivered to your door. Order now!`,
      };
    },
    [storeName],
  );

  const generateDescription = useCallback(
    async (input: SubmitInput): Promise<{ ar: string; en: string; isFallback: boolean }> => {
      // The Gemini-backed generate endpoint requires a valid WhatsApp number and
      // at least one real (non-COD) payment method. If we don't have both, skip
      // the doomed request and use the local fallback directly.
      if (!normalizeWhatsApp(input.whats) || !hasRealPayment(input.payForm)) {
        return { ...buildFallback(input), isFallback: true };
      }
      const firstRule = buildShippingRules(input.shipping)[0];
      const payload: GenerateSetupInput = {
        businessType: input.productsLabel || "products",
        acceptsCOD: input.payForm.cod,
        shippingGovernorates: input.shipping.map((r) => r.govId).filter(Boolean),
        shippingPrice: firstRule?.price ?? 0,
        estimatedDeliveryDays: firstRule?.estimatedDeliveryDays ?? 1,
        wantsDeposit: false,
        ...buildGeneratePaymentFields(input.payForm),
      };
      try {
        const result = await generateMutation.mutateAsync(payload);
        const ar = (result.storeDescription || "").trim();
        const en = (result.storeDescriptionEn || "").trim();
        if (!ar && !en) throw new Error("empty");
        const fb = buildFallback(input);
        // Fall back per-language if the model returned only one of the two.
        return { ar: ar || fb.ar, en: en || fb.en, isFallback: false };
      } catch {
        return { ...buildFallback(input), isFallback: true };
      }
    },
    [generateMutation, buildFallback],
  );

  const saveDescription = useCallback(
    async (descriptionAr: string, descriptionEn: string) => {
      const ar = descriptionAr.trim();
      const en = descriptionEn.trim();
      if (!ar && !en) return;
      try {
        await updateStore.mutateAsync({
          storeSlug,
          description: ar,
          descriptionEn: en,
        });
      } catch {
        // Best-effort — the seller can edit the description later in settings.
      }
    },
    [storeSlug, updateStore],
  );

  return { status, error, persist, generateDescription, saveDescription };
}
